import { lazy } from 'react'
import { Route } from 'react-router-dom'
import { lazyMitNeuladen } from '../../utils/lazyMitNeuladen'
import ProtectedRoute from '../../ProtectedRoute'

/**
 * musterroute — the two-part gate that keeps `/design/muster` out of a
 * production build (G0.11, plan §4.1).
 *
 * @description
 *   The Musterroute is a TEST surface, not a product surface: it renders every
 *   list zone with real components and fixture data so the browser probes of
 *   `frontend/tests/listen/muster.spec.ts` have something to measure. It carries
 *   no sidebar entry — the same house form as the component catalogue `/design`
 *   (`App.js`, „design" route) — and it must not exist for a user.
 *
 *   🔴 **Two gates, and BOTH are named (Nachbesserung 1, W10):**
 *
 *   1. `musterrouteAktiv()` — the route is **not registered** when
 *      `NODE_ENV === 'production'`; `musterrouteEintrag()` then returns `null`.
 *   2. `ListenMuster` is loaded through **`lazy()`** — the house pattern of the
 *      catalogue (`App.js`, `DesignCatalog`), not a static top import.
 *
 *   🔴 **Why the second gate is not decoration:** a static top import would ship
 *   the module in the **main bundle** for as long as Terser does not drop it.
 *   The precise sentence is therefore: in a production build **no route exists**,
 *   and the module sits in a **chunk of its own that is never fetched** — NOT
 *   „the module does not exist". The `NODE_ENV` gate alone does not carry that
 *   sentence; `lazy()` and the gate together do. A grill measured exactly this
 *   difference (W10).
 *
 *   **Widerleger (Regel 17):** does the `listen` run ever drive a PRODUCTION
 *   build, `/design/muster` answers 404 and the probes are not drivable. Then the
 *   `NODE_ENV` gate falls away and the catalogue path stays (reachable,
 *   unlinked). Regel 8 drives against the CRA **development** server
 *   (`npm start`, `NODE_ENV=development`) — the gate is invisible in ordinary
 *   operation.
 *
 *   ⚠️ **The environment is a PARAMETER, not a read of `process.env` inside the
 *   function body.** A pure function with a default is testable in jsdom (where
 *   `process.env.NODE_ENV` is `'test'` and cannot be moved), and the Jest probe
 *   `ListenMuster.test.jsx` calls it with `'production'` directly. The default
 *   keeps `App.js` a one-liner.
 *
 * @useWhen
 *   - `App.js` — the ONE call site, inside the same `<Routes>` as `design`
 *   - A Jest probe that has to show the entry is EMPTY in a production build
 *
 * @dontUseFor
 *   - A route that a user is meant to reach — this one is a measuring surface
 *
 * @related
 *   - `ListenMuster` — the page itself (`frontend/src/design/muster/ListenMuster.jsx`)
 *   - `DesignCatalog` — the same house form for `/design`
 *
 * @reuseHints
 *   - Any future development-only route takes this shape: a pure gate function,
 *     a `lazy()` module and an entry function that returns `null` in production
 *
 * @props
 *   musterrouteAktiv(env)   — the rule: `env !== 'production'`
 *   musterrouteEintrag(env) — the `<Route>` element, or `null` in production
 *   ListenMuster            — the `lazy()` module of the page
 */

/** The rule, as a pure function — the environment is an ARGUMENT (see above). */
export function musterrouteAktiv(env = process.env.NODE_ENV) {
  return env !== 'production'
}

/**
 * The page, behind `lazy()`: a chunk of its own that is never fetched while no
 * route points at it (plan §4.1).
 */
export const ListenMuster = lazy(lazyMitNeuladen(() => import('./ListenMuster')))

/**
 * The route-table fragment: ONE entry in development, NONE in a production
 * build. `App.js` renders the return value verbatim.
 */
export function musterrouteEintrag(env = process.env.NODE_ENV) {
  if (!musterrouteAktiv(env)) return null
  return (
    <Route
      path="design/muster"
      element={
        <ProtectedRoute>
          <ListenMuster />
        </ProtectedRoute>
      }
    />
  )
}
